// src/components/Layout/SidebarMenu.jsx
import { useState, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';

export default function SidebarMenu({
  menuItems = [
    { label: 'My Teams', step: 4 },
    { label: 'Join Competitions', step: 0 },
  ],
  useSteps = true,
  step,
  setStep,
  selectedKey,
  onItemSelect,
  header = null,
  footer = null,
  className = '',
  adminVisible = false,
  adminPath = '/admin/competitions',
  adminLabel = 'Admin Portal',

  // global active/inactive styles
  activeClasses = 'bg-yellow-50 text-yellow-700 font-semibold border-yellow-500 shadow-sm',
  inactiveClasses = 'text-gray-800 hover:bg-gray-100 border-transparent',
}) {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  // Figure out which item should be considered active
  const computedSelectedKey = useMemo(() => {
    if (selectedKey != null) return selectedKey;

    if (useSteps) {
      const byStep = menuItems.find((i) => typeof step === 'number' && i.step === step);
      if (byStep) return byStep.key ?? `step-${byStep.step}`;
      return undefined;
    } else {
      const byRoute = menuItems.find((i) => {
        const toPath = i.to ?? i.link;
        return toPath && location.pathname.startsWith(toPath);
      });
      if (byRoute) return byRoute.key ?? (byRoute.to ?? byRoute.link);
      return undefined;
    }
  }, [menuItems, selectedKey, step, useSteps, location.pathname]);

  const handleItemClick = (item) => {
    if (item.onClick) item.onClick();
    if (useSteps && typeof item.step === 'number' && typeof setStep === 'function') {
      setStep(item.step);
    }
    if (onItemSelect) onItemSelect(item);
    setOpen(false);
  };

  const Nav = (
    <nav className="flex flex-col gap-1 p-2 w-max">
      {menuItems.map((item, index) => {
        const key = item.key ?? item.to ?? item.link ?? item.step ?? index;

        // ✅ In step mode, compare the actual step numbers
        const isSelected = useSteps
          ? (typeof step === 'number' && typeof item.step === 'number' && step === item.step)
          : (computedSelectedKey === key);

        const base =
          'relative text-left rounded px-3 py-2 text-sm transition flex items-center gap-2 disabled:opacity-50 whitespace-nowrap border-l-4';

        if (!useSteps && (item.to ?? item.link)) {
          const toPath = item.to ?? item.link;
          return (
            <Link
              key={key}
              to={toPath}
              onClick={() => handleItemClick(item)}
              className={`${base} ${isSelected ? activeClasses : inactiveClasses} focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-400`}
              aria-current={isSelected ? 'page' : undefined}
            >
              {item.icon ?? null}
              <span>{item.label}</span>
            </Link>
          );
        }

        return (
          <button
            key={key}
            type="button"
            disabled={item.disabled}
            onClick={() => handleItemClick(item)}
            className={`${base} ${isSelected ? activeClasses : inactiveClasses} focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-400`}
            aria-pressed={isSelected ? true : false}
          >
            {item.icon ?? null}
            <span>{item.label}</span>
          </button>
        );
      })}

      {adminVisible && (
        <Link
          to={adminPath}
          onClick={() => setOpen(false)}
          className={`text-left rounded px-3 py-2 text-sm transition ${inactiveClasses} hover:bg-gray-100`}
        >
          {adminLabel}
        </Link>
      )}
    </nav>
  );

  return (
    <>
      {/* Mobile hamburger button */}
      <button
        type="button"
        className="md:hidden fixed left-1 top-1 z-50 inline-flex items-center justify-center rounded-md p-2 bg-none"
        onClick={() => setOpen(true)}
        aria-label="Open menu"
      >
        <Bars3Icon className="h-6 w-6" />
      </button>

      {/* Mobile backdrop */}
      <div
        className={`fixed inset-0 z-40 bg-black/30 transition-opacity md:hidden ${
          open ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
        onClick={() => setOpen(false)}
      />

      {/* Drawer (mobile) / Sidebar (desktop) */}
      <aside
        className={`
          fixed z-50 md:z-auto
          top-0 left-0
          h-screen md:h-[calc(100vh-3rem)]  /* 3rem ≈ your header height (pt-12). Adjust if needed */
          w-64
          bg-white shadow
          transform transition-transform duration-200
          md:transform-none
          ${open ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
          pt-16 md:pt-0 md:sticky md:top-12  /* stick under the header on desktop */
          rounded-none md:rounded-md
          ${className}
        `}
      >
        <div className="md:hidden flex justify-end p-2">
          <button
            type="button"
            className="rounded-md p-2"
            onClick={() => setOpen(false)}
            aria-label="Close menu"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {header ? <div className="p-2 border-b">{header}</div> : null}
        {Nav}
        {footer ? <div className="mt-auto p-2 border-t">{footer}</div> : null}
      </aside>
    </>
  );
}
